import {
  Component,
  ChangeDetectionStrategy,
  Injectable,
  OnInit,
  inject,
  input,
  booleanAttribute,
  computed,
} from '@angular/core';
import { ButtonComponent } from '@cue/assist/ui/buttons';
import { LoadingComponent } from '@cue/loading';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { cueDirections, cueAdd, cueCheck, cueOrders, cueArrowRight, cueClose, IconComponent } from '@cue/ui/icons';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { translation } from '@cue/translations';
import {
  allResources,
  AppState,
  AssistOrderGridItem,
  AssistOrderItem,
  AuthService,
  ConfigService,
  DataService,
  getTextKeyAndClass,
  IdentifiedAssistEvent,
  injectAreas,
  OrdersService,
  ResourceService,
  ServiceSelectDataItem,
} from '@assist/shared/data';
import { AssistResourceBM, OrderActionRequest } from '@cue/api';
import { NewResourceMapComponent } from '@assist/ui/new-floorplans';
import { DatetimeInfoComponent } from '@cue/assist/ui/datetime-info';
import { ImageWidthPipe, LocalizedStringPipe, RtlDirective } from '@cue/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { switchMap, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { addMinutes } from 'date-fns';
import { Router } from '@angular/router';
import { ModalBodyComponent, ModalComponent, ModalFooterComponent, ModalHeaderComponent } from '@cue/modal';
import { WorkingHoursComponent } from '@cue/ui';
import { LicensingService } from '@cue/licensing';
import { CustomUnsafeSanitizePipe } from '@cue/security';
import { localized } from '@cue/translate';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FileGalleryComponent } from '@cue/ui/file-gallery';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ReservationDetailOrdersTabComponent,
  ReservationDetailResourceTabComponent,
  ServiceSelectComponent,
} from '@assist/info-tabs';
import { ResourceDetail } from '@cue/data';
import { DateFormatPipe, SubjectPipe, TimeFormatPipe } from '@cue/pipes';
import { ResourceTitlePipe } from '@assist/ui/pipes';
import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { OpenInNavigationButtonComponent } from '@cue/navigation-widgets';
import { ExpansionPanelComponent, ExpansionPanelTitleDirective } from '@progress/kendo-angular-layout';

interface Attendee {
  name: string;
  email: string;
  status: string;
  initials: string;
  color: string;
  required: boolean;
}

const randomColors = [
  '#FFF3B0',
  '#B2BD7E',
  '#7FC6A4',
  '#FAF33E',
  '#EEF5DB',
  '#FE5F55',
  '#AFBBF2',
  '#61C9A8',
  '#ED9B40',
];

interface OrderResourceDetailState {
  loading: boolean;
  reservation:
    | (IdentifiedAssistEvent & {
        optionalAttendees: any[];
        requiredAttendees: any[];
      })
    | null;
}

const initialState: OrderResourceDetailState = {
  loading: false,
  reservation: null,
};

@Injectable()
export class OrderResourceDetailStore extends ComponentStore<OrderResourceDetailState> {
  private dataService = inject(DataService);
  private store = inject<Store<AppState>>(Store<AppState>);
  protected translocoService = inject(TranslocoService);

  constructor() {
    super(initialState);
  }

  readonly loadReservationDetails = this.effect(
    (
      reservationId$: Observable<{
        icalUid: string;
        from: Date;
        to: Date;
      }>
    ) => {
      return reservationId$.pipe(
        concatLatestFrom(() => this.store.select(allResources)),
        switchMap(([action, allResources]) => {
          this.patchState({ loading: true });
          return this.dataService.getReservationByICalIUid(action.from, action.to, allResources.data, action.icalUid);
        }),
        tap((reservation: any) => {
          let res = reservation;
          if (res) {
            const attendees = res.attendees
              .filter((x) => x.resource !== true)
              .map(
                (a, index) =>
                  ({
                    name: a.emailAddress.name ?? a.emailAddress.address,
                    email: a.emailAddress.address,
                    status: a.status.response,
                    required: a.type == 'required',
                    initials:
                      a.emailAddress.name != null
                        ? a.emailAddress.name.trim().split(' ').length > 1
                          ? a.emailAddress.name.trim().split(' ')[0][0] + a.emailAddress.name.trim().split(' ')[1][0]
                          : '@'
                        : '@',
                    color: randomColors[index % (randomColors.length - 1)],
                  }) as Attendee
              );

            res = {
              ...res,
              optionalAttendees: attendees.filter((x) => !x.required),
              requiredAttendees: attendees.filter((x) => x.required),
            };
          }

          this.patchState({ reservation: res, loading: false });
        })
      );
    }
  );
}

export type OrdersResourceDetailData = {
  resources: ResourceDetail[];
  existingOrder?: AssistOrderGridItem;
  hideLocation?: boolean;
};

@Component({
  selector: 'assist-orders-resource-detail',
  templateUrl: './orders-resource-detail.component.html',
  styleUrls: ['./orders-resource-detail.component.scss'],
  imports: [
    RtlDirective,
    TranslocoPipe,
    IconComponent,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    ReactiveFormsModule,
    DateFormatPipe,
    TimeFormatPipe,
    LoadingComponent,
    ButtonComponent,
    ServiceSelectComponent,
    NewResourceMapComponent,
    DatetimeInfoComponent,
    ResourceTitlePipe,
    ImageWidthPipe,
    WorkingHoursComponent,
    LocalizedStringPipe,
    CustomUnsafeSanitizePipe,
    FileGalleryComponent,
    OpenInNavigationButtonComponent,
    SubjectPipe,
    NgStyle,
    NgClass,
    NgTemplateOutlet,
    AsyncPipe,
    ReservationDetailResourceTabComponent,
    ReservationDetailOrdersTabComponent,
    ExpansionPanelComponent,
    ExpansionPanelTitleDirective,
  ],
  providers: [OrderResourceDetailStore, LocalizedStringPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdersResourceDetailComponent implements OnInit {
  protected licensingService = inject(LicensingService);
  private router = inject(Router);
  areas = injectAreas();
  protected store = inject(OrderResourceDetailStore);
  public configService = inject(ConfigService);
  private fb = inject(FormBuilder);
  protected authService = inject(AuthService);
  protected translocoService = inject(TranslocoService);
  protected resourceService = inject(ResourceService);
  private ordersService = inject(OrdersService);
  vm = toSignal(this.store.select((s) => s));
  localizedStringPipe = inject(LocalizedStringPipe);
  translation = translation;
  activeTab = 0;

  tabs = computed(() => {
    let array = [];
    if (this.modalData.existingOrder?.iCalUid) {
      if (!this.modalData.existingOrder?.iCalUid) {
        array.push(this.translocoService.translate(translation.orders.serviceRequests));
      } else {
        array.push(this.translocoService.translate(translation.orders.orders));
      }
      array.push(this.translocoService.translate(translation.reservationPopup.reservation));
    } else {
      if (!this.modalData.existingOrder?.iCalUid) {
        array.push(this.translocoService.translate(translation.orders.serviceRequests));
      } else {
        array.push(this.translocoService.translate(translation.orders.orders));
      }
    }
    if (!this.modalData.hideLocation) {
      array.push(this.translocoService.translate(translation.orders.filterResources));
    }

    return array;
  });

  title = computed(() => {
    return !this.modalData.existingOrder
      ? this.translocoService.translate(translation.orders.newOrderTo) +
          ' ' +
          (this.localizedStringPipe.transform(this.modalData.resources[0].displayNameForApp) ??
            this.localizedStringPipe.transform(this.modalData.resources[0].displayName) ??
            this.modalData.resources[0].name)
      : this.modalData.existingOrder?.iCalUid
        ? this.translocoService.translate(translation.orders.newOrderToEvent) +
          ' ' +
          (this.modalData.existingOrder.resourceDisplayNameForApp ?? this.modalData.existingOrder.resourceName)
        : this.translocoService.translate(translation.orders.newOrderTo) +
          ' ' +
          (this.localizedStringPipe.transform(this.modalData.existingOrder.resourceDisplayNameForApp) ??
            this.localizedStringPipe.transform(this.modalData.existingOrder.resourceDisplayName) ??
            this.modalData.existingOrder.resourceName);
  });

  modal = inject(DialogRef);
  modalData = inject<OrdersResourceDetailData>(DIALOG_DATA);
  form: FormGroup<{ note: FormControl<string>; items: FormArray }> = null;
  orders: FormArray = null;
  adding = false;
  state: 'view' | 'edit' | 'loading' = 'edit';
  apiURL = this.configService.value.apiURL;

  ICONS = {
    cueDirections,
    cueAdd,
    cueCheck,
    cueOrders,
    cueArrowRight,
    cueClose,
  };

  ngOnInit(): void {
    this.orders = this.fb.array([]);
    this.form = this.fb.group({
      note: [],
      items: this.orders,
    });

    if (this.modalData.existingOrder) {
      if (this.modalData.existingOrder?.iCalUid) {
        const from = addMinutes(new Date(this.modalData.existingOrder.date), -30);
        const to = addMinutes(new Date(this.modalData.existingOrder.date), 30);
        this.store.loadReservationDetails({
          from: from,
          to: to,
          icalUid: this.modalData.existingOrder.iCalUid,
        });
      }
    }
  }

  onNavigate(resource: AssistResourceBM) {
    ///navigation?targetResourceId=1025
    this.modal?.close();
    this.router.navigate(['/navigation'], {
      queryParams: {
        targetResourceId: resource.idForFiltering,
      },
    });
  }

  changeTab(activeTab) {
    this.activeTab = activeTab;
  }

  createNewOrderItem(item: ServiceSelectDataItem) {
    const group: FormGroup<{
      serviceId: FormControl<number>;
      name: FormControl<string>;
      imageUrl: FormControl<string>;
      noteAllowed: FormControl<boolean>;
      note?: FormControl<string>;
      noteMinLength?: FormControl<number>;
    }> = this.fb.group({
      serviceId: new FormControl(item.id, { nonNullable: true }),
      name: new FormControl(localized(item.name, this.translocoService), { nonNullable: true }),
      imageUrl: new FormControl(item.imageUrl, { nonNullable: true }),
      noteAllowed: new FormControl(item.noteAllowed, { nonNullable: true }),
    });

    if (item.noteAllowed) {
      group.addControl('note', new FormControl('', { nonNullable: true }));
      if (item.noteMinLength > 0) {
        group.get('note').addValidators([Validators.required, Validators.minLength(item.noteMinLength)]);
      }
      group.addControl('noteMinLength', new FormControl(item.noteMinLength));
    }
    return group;
  }

  add() {
    this.adding = true;
  }

  onCancel() {
    this.adding = false;
  }

  onSelect(item: ServiceSelectDataItem) {
    this.orders.push(this.createNewOrderItem(item));
    this.adding = false;
  }

  order() {
    if (this.orders.length == 0) {
      return;
    }
    const email = this.authService.getEmail();
    if (this.form.valid) {
      this.state = 'loading';
      const value = this.form.value;
      const request: OrderActionRequest = {
        order: {
          note: value.note,
          items: value.items.map((oi) => ({
            note: oi.note,
            cancelled: false,
            serviceId: oi.serviceId,
          })),
        },
        data: {
          doneBy: 'assist',
          userEmail: email,
          resourceUsername: this.modalData.resources[0].username,
        },
      };
      this.ordersService.createEventLessOrder(request).subscribe(() => {
        this.modal.close(true);
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  remove(index: number) {
    this.orders.removeAt(index);
  }

  getServiceInfo(item: AssistOrderItem) {
    return this.configService.value.services.find((x) => x.id === item.serviceId);
  }

  protected readonly getTextKeyAndClass = getTextKeyAndClass;
}
